// Questionnaire.tsx

import {Box, Button, CircularProgress, LinearProgress, Typography, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions} from "@mui/material";
import Question from "../components/question/Question";
import { QuestionnaireConfig, QuestionType } from "../data/QuestionConfig";
import { useStoreDispatch, useStoreSelector } from "../store/hooks";
import { useTranslation } from "react-i18next";
import {
    addNewsletterSubscription,
    enterNumericQuestionAnswer,
    evaluateAnswers,
    filteredQuestions,
    filterRelevantQuestions,
    nextQuestion,
    previousQuestion,
    QuestionnaireStates,
    reset,
    selectAllAnswers,
    selectCurrentQuestionId,
    selectCurrentQuestionIndex,
    selectQuestionnaireState, sendDataToWebhook, sendRecommendationEmail,
    submitAnswers,
    updateQuestionAnswer
} from "../store/questionnaireSlice";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import WarningDialog from "../components/question/WarningDialog";
import { ArrowBack } from "@mui/icons-material";
import { SQToggleButton } from "../components/SQToggleButton";
import {theme} from "../theme.ts";

function GoToResultsButton({ state }: { state: QuestionnaireStates }) {
    const { t } = useTranslation();
    const isSubmitting = state === QuestionnaireStates.Submitting;
    return (
        <Box display={"flex"} alignItems={"center"} gap={3}>
            {t("to_results")}
            {isSubmitting ? <CircularProgress color={"inherit"} size={"1em"} /> : null}
        </Box>
    );
}

export interface ErrorMessage {
    show: boolean;
    title: string;
    content: string;
}

interface CancelDialogState {
    show: boolean;
}

export default function Questionnaire() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [errorMessage, setErrorMessage] = useState<ErrorMessage>({ show: false, content: "", title: "" });

    const dispatch = useStoreDispatch();
    const { t } = useTranslation();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const currentQuestionId = useStoreSelector(selectCurrentQuestionId);
    const currentQuestionIndex = useStoreSelector(selectCurrentQuestionIndex);
    const questionnaireState = useStoreSelector(selectQuestionnaireState);
    const allAnswers = useStoreSelector(selectAllAnswers);
    const questionKeysForSelectedGroups = useStoreSelector(filteredQuestions);

    const isFirstQuestion = currentQuestionIndex === 0;
    const isLastQuestion = currentQuestionId !== 'article_groups' && currentQuestionIndex === questionKeysForSelectedGroups.length - 1;

    const currentQuestionConfig = QuestionnaireConfig[currentQuestionId];
    const currentAnswers = allAnswers[currentQuestionId];
    const isOptional = currentQuestionConfig.optional;

    const [agreePrivacy, setAgreePrivacy] = useState(false);
    const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
    const [email, setEmail] = useState('');
    const [region, setRegion] = useState('');
    const [cancelDialog, setCancelDialog] = useState<CancelDialogState>({ show: false });

    function onBackClicked() {
        if (isFirstQuestion) {
            // Show confirmation dialog for canceling
            setCancelDialog({ show: true });
        } else {
            dispatch(previousQuestion());
        }
    }

    function onCancelConfirm() {
        // Clear all questionnaire-related parameters and go back to home
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('onetimetoken');
        newSearchParams.delete('questionnaireType');
        newSearchParams.delete('questionnaireTypes');
        newSearchParams.delete('bicycleType');
        
        // Reset the questionnaire state
        dispatch(reset());
        
        setCancelDialog({ show: false });
        navigate({ pathname: "/", search: newSearchParams.toString() });
    }

    function onCancelCancel() {
        setCancelDialog({ show: false });
    }

    const isNextButtonEnabled = () => {
        if (currentQuestionConfig.questionId === "privacy_agreement") {
            return agreePrivacy;
        } else if (currentQuestionConfig.questionType === QuestionType.singleChoice || currentQuestionConfig.questionType === QuestionType.multiChoice) {
            return isOptional || (currentAnswers !== undefined && currentAnswers.length !== 0);
        }  else {
            return isOptional
                || (currentQuestionConfig.inputConfigs!.every(inputConfig => {
                    return allAnswers[inputConfig.inputId] !== undefined && allAnswers[inputConfig.inputId].length > 0
                }));
        }
    };

    async function onNextClicked() {
        if (isNextButtonEnabled()) {
            if (isLastQuestion) {
                try {
                    await dispatch(submitAnswers()).unwrap();
                    await dispatch(evaluateAnswers()).unwrap();

                    if (email) {
                        dispatch(sendRecommendationEmail({ email, region })).unwrap();
                    }

                    if (subscribeNewsletter && email) {
                        dispatch(addNewsletterSubscription({ email, region })).unwrap();
                    }

                    dispatch(sendDataToWebhook({email, subscribeNewsletter}));

                    navigate({
                        pathname: "/results",
                        search: searchParams.toString(),
                    });
                } catch (e) {
                    setErrorMessage({
                        title: t("answers_sending_error_title"),
                        content: t("answers_sending_error_content"),
                        show: true
                    });
                    console.error(e);
                }
            } else {
                dispatch(nextQuestion());
            }
        } else {
            setErrorMessage({ show: true, title: t("non_optional_warning_title"), content: "" });
        }
    }

    async function onSkipMeasurementsClicked() {
        if(currentQuestionConfig.questionType === QuestionType.singleChoice || currentQuestionConfig.questionType === QuestionType.multiChoice) {
            dispatch(updateQuestionAnswer([]));
        } else {
            currentQuestionConfig.inputConfigs!.forEach(inputConfig => {
                dispatch(enterNumericQuestionAnswer([inputConfig.inputId, undefined]));
            });
        }
        if (isLastQuestion) {
            try {
                await dispatch(submitAnswers()).unwrap();
                await dispatch(evaluateAnswers()).unwrap();
                navigate({ pathname: "/results", search: searchParams.toString() });
            } catch (e) {
                setErrorMessage({
                    title: t("answers_sending_error_title"),
                    content: t("answers_sending_error_content"),
                    show: true
                });
                console.error(e);
            }
        } else {
            dispatch(filterRelevantQuestions());
            dispatch(nextQuestion());
        }
    }

    const showSkipMeasurementsButton =
        currentQuestionConfig.questionType === QuestionType.numeric ||
        currentQuestionConfig.questionType === QuestionType.multiNumeric || currentQuestionConfig.optional;

    function isSubmitting() {
        return questionnaireState === QuestionnaireStates.Submitting;
    }

    const isGoToResultsButtonDisabled = () => {
        return subscribeNewsletter && (!email) || !agreePrivacy;
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight:'85vh', justifyContent: 'space-between' }}>
                <Box sx={{ width: '100%', mb: 3 }}>
                    <LinearProgress
                        variant="determinate"
                        value={(currentQuestionIndex + 1) / questionKeysForSelectedGroups.length * 100}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: (theme) => theme.palette.primary.main,
                                transition: theme => theme.transitions.create('transform', {
                                    duration: theme.transitions.duration.standard
                                })
                            }
                        }}
                    />
                </Box>

                <Question
                    questionConfig={currentQuestionConfig}
                    onNewsletterChange={setSubscribeNewsletter}
                    onEmailChange={setEmail}
                    onNameChange={(name) => console.log("Name changed:", name)}
                    onRegionChange={setRegion}
                    onAgreePrivacyChange={setAgreePrivacy}
                />

                <Box
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    justifyContent="space-between"
                    alignItems={isMobile ? "stretch" : "center"}
                    p={0}
                    gap={isMobile ? 2 : 0}
                >
                    <Button
                        variant="text"
                        startIcon={isFirstQuestion ? null : <ArrowBack />}
                        onClick={onBackClicked}
                        sx={{
                            fontSize: theme.typography.button.fontSize,
                            textTransform: 'uppercase',
                            color: 'black',
                            px: 2,
                            '& .MuiButton-startIcon': {
                                marginRight: '4px',
                            },
                            alignSelf: isMobile ? "flex-center" : "auto",
                        }}
                    >
                        {isFirstQuestion ? t("cancel") : t("back")}
                    </Button>

                    <Box
                        display="flex"
                        flexDirection={isMobile ? "column" : "row"}
                        alignItems={isMobile ? "stretch" : "center"}
                        gap={isMobile ? 2 : "16px"}
                    >
                        {showSkipMeasurementsButton && (
                            <SQToggleButton
                                selected={false}
                                onClick={onSkipMeasurementsClicked}
                                sx={{
                                    marginRight: isMobile ? 0 : "16px",
                                    width: isMobile ? "100%" : "auto",
                                }}
                            >
                                {t("continue_without_values")}
                            </SQToggleButton>
                        )}

                        <Button
                            variant="contained"
                            onClick={onNextClicked}
                            disabled={isSubmitting() || !isNextButtonEnabled() || (isLastQuestion && isGoToResultsButtonDisabled())}
                            sx={{
                                width: isMobile ? "100%" : "auto",
                            }}
                        >
                            <Typography fontSize={theme.typography.button.fontSize} px={2}>
                                {isLastQuestion ? <GoToResultsButton state={questionnaireState} /> : t("next")}
                            </Typography>
                        </Button>
                    </Box>
                </Box>
                <WarningDialog errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
                
                {/* Cancel Confirmation Dialog */}
                <Dialog open={cancelDialog.show} onClose={onCancelCancel}>
                    <DialogTitle>{t("cancel_questionnaire_title")}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t("cancel_questionnaire_content")}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onCancelCancel}>
                            {t("cancel_questionnaire_no")}
                        </Button>
                        <Button onClick={onCancelConfirm} color="primary" variant="contained">
                            {t("cancel_questionnaire_yes")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}
